/**
 * IdentityTenantsPageV2 — Phase 6E (P0-IDENT).
 *
 * Tenant-scope landing page. The default view is the INHERITANCE PANE:
 * a matrix showing every tenant × every settable dimension, with each
 * cell rendered as either ↑ "inherit from Org" or ✎ "override".
 *
 * Inheritance model (locked 2026-05):
 *   - Every setting starts INHERITED from the parent scope
 *   - Any scope can opt to override per-setting (not per-scope)
 *   - Order: Org → Tenant → Agent
 *   - A Tenant override at a setting wins over the Org default for that
 *     tenant; an Agent override wins over its Tenant's resolved value
 *
 * Visual language:
 *   - inherit cells     — muted text, blue ↑ arrow
 *   - override cells    — purple text, ✎ pencil + the overriding value
 *   - override rows     — tinted background + "override" pill on tenant name
 *
 * Data: stub data shaped after GET /api/v1/identity/tenants, which is
 * implemented as IdentityScopeController#listTenants in Phase 6F.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, Bot, ArrowUp, Pencil, ChevronRight, ArrowRight,
  Filter, Plus, AlertTriangle,
} from 'lucide-react';
import { V2PageShell } from '../components/v2/V2PageShell';
import { Card } from '../components/v2/Card';
import { Button } from '../components/v2/Button';
import { Pill } from '../components/v2/Pill';
import { EmptyState } from '../components/v2/EmptyState';
import { Skeleton } from '../components/v2/Skeleton';
import { listIdentityTenants } from '../services/api';

/* ────────────────────────────────────────────────────────────────
   Stage 5-5 — Tenant inheritance data now sourced from the backend
   via listIdentityTenants(). The backend projects from the canonical
   tenants × instances tables; this page renders the flat matrix
   shape. Hierarchical pane (tenants expand to show instances) is a
   future restructure.

   The stub TENANTS constant below is REMOVED — the page is fully
   data-driven now. The legacy const has been preserved in source
   control history if needed.
   ──────────────────────────────────────────────────────────────── */

/* Cell shape from the backend (per tenant per dimension):
 *   { inherit: true } or { inherit: false, value: '<display string>' }.
 * Six dimensions tracked: trustDomain, svidTtl, rotation, spire,
 * compliance, federation. */


const COLUMNS = [
  { key: 'trustDomain', label: 'Trust domain' },
  { key: 'svidTtl',     label: 'SVID TTL' },
  { key: 'rotation',    label: 'Rotation' },
  { key: 'spire',       label: 'SPIRE' },
  { key: 'compliance',  label: 'Compliance' },
  { key: 'federation',  label: 'Federation' },
];

/* ────────────────────────────────────────────────────────────────
   Cell renderer — handles inherit vs override.
   Override cells use trust-tone tinting; inherit cells stay muted.
   ──────────────────────────────────────────────────────────────── */

function Cell({ cell }) {
  if (cell.inherit) {
    return (
      <div className="text-[11px] text-v2-ink-muted flex items-center gap-1">
        <ArrowUp size={11} className="text-v2-action" aria-hidden="true" />
        <span>inherit</span>
      </div>
    );
  }
  return (
    <div className="text-[11px] text-v2-trust font-semibold flex items-center gap-1">
      <Pencil size={11} aria-hidden="true" />
      <span>{cell.value}</span>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
   IdentityTenantsPageV2 — top-level export
   ──────────────────────────────────────────────────────────────── */

export default function IdentityTenantsPageV2() {
  const navigate = useNavigate();
  /* Stage 5-5 — data now comes from the backend via
   * listIdentityTenants(). Backend projects from real tenants ×
   * instances tables; this page renders the flat matrix shape. */
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    listIdentityTenants().then((res) => {
      if (cancelled) return;
      if (res?.ok === false) {
        setError(res.error || 'Could not load tenant inheritance.');
        setData(null);
      } else {
        setData(res?.data || null);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const tenants   = data?.tenants || [];
  const total     = data?.total ?? 0;
  const overrides = data?.overrides ?? 0;
  const inherits  = data?.inherits ?? 0;
  const subtitleStr = loading
    ? 'Loading…'
    : `${total} tenant${total === 1 ? '' : 's'} · ${inherits} fully inherit Org defaults · ${overrides} override`;

  return (
    <V2PageShell
      title="Tenant inheritance"
      subtitle={subtitleStr}
      backTo="/identity"
      backLabel="Back to Identity"
      tags={
        <>
          <Pill tone="trust"  size="sm">Tenant</Pill>
          <Pill tone="action" size="sm" dot>SPIFFE plane</Pill>
        </>
      }
      actions={
        <>
          <Button variant="secondary" size="sm" iconLeft={<Filter size={14} />}>Filter</Button>
          <Button variant="primary"   size="sm" iconLeft={<Plus   size={14} />}>New tenant</Button>
        </>
      }
    >
      {/* Scope chain banner — visualises the inheritance order        */}
      <Card padding="sm" className="mb-4">
        <div className="flex items-center gap-3">
          <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold">
            Scope chain
          </div>
          <div className="flex items-center gap-2 flex-1">
            <Pill tone="action" size="sm">
              <Building2 size={11} className="inline -mt-0.5 mr-1" aria-hidden="true" />
              Organization
            </Pill>
            <ArrowRight size={13} className="text-v2-ink-muted" aria-hidden="true" />
            <Pill tone="trust" size="sm">
              <Users size={11} className="inline -mt-0.5 mr-1" aria-hidden="true" />
              Tenant
            </Pill>
            <ArrowRight size={13} className="text-v2-ink-muted" aria-hidden="true" />
            <Pill tone="success" size="sm">
              <Bot size={11} className="inline -mt-0.5 mr-1" aria-hidden="true" />
              Agent
            </Pill>
          </div>
          <div className="text-[11px] text-v2-ink-secondary">
            Each setting inherits from parent unless overridden
          </div>
        </div>
      </Card>

      {/* Loading + error + empty states ─────────────────────────── */}
      {loading && (
        <Card padding="md">
          <Skeleton height={48} /><div className="mt-2" />
          <Skeleton height={48} /><div className="mt-2" />
          <Skeleton height={48} />
        </Card>
      )}

      {!loading && error && (
        <Card>
          <EmptyState
            icon={<AlertTriangle />}
            title="Could not load tenant inheritance"
            description={error}
            primaryAction={
              <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
                Retry
              </Button>
            }
          />
        </Card>
      )}

      {!loading && !error && tenants.length === 0 && (
        <Card>
          <EmptyState
            icon={<Users />}
            title="No tenants in this org yet"
            description="Tenants are created in the Multi-Tenancy section. Once your org has tenants, their identity-plane inheritance state will appear here."
            primaryAction={
              <Button variant="primary" size="sm" iconLeft={<Plus size={14} />}
                      onClick={() => navigate('/multi-tenancy')}>
                Go to Multi-Tenancy
              </Button>
            }
          />
        </Card>
      )}

      {/* Inheritance table — the page's main artefact                 */}
      {!loading && !error && tenants.length > 0 && (
      <Card padding="none" className="overflow-hidden">
        <div className="grid grid-cols-[1.5fr_0.9fr_0.9fr_0.9fr_0.9fr_0.9fr_0.9fr_0.5fr] gap-2 px-4 py-2.5 bg-v2-muted text-[10px] uppercase tracking-wide text-v2-ink-muted font-semibold">
          <div>Tenant</div>
          {COLUMNS.map((c) => <div key={c.key}>{c.label}</div>)}
          <div></div>
        </div>

        {tenants.map((t) => (
          <div
            key={t.id}
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/identity/tenants/${t.id}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/identity/tenants/${t.id}`);
              }
            }}
            className={
              'grid grid-cols-[1.5fr_0.9fr_0.9fr_0.9fr_0.9fr_0.9fr_0.9fr_0.5fr] gap-2 px-4 py-3 border-t border-v2-border-subtle items-center cursor-pointer hover:bg-v2-muted/50 transition-colors ' +
              (t.hasOverride ? 'bg-v2-trust-soft/30' : '')
            }
          >
            <div>
              <div className="text-[13px] font-semibold text-v2-ink flex items-center gap-1.5">
                {t.name}
                {t.hasOverride && <Pill tone="trust" size="sm">override</Pill>}
              </div>
              <div className="text-[11px] text-v2-ink-muted">
                {t.agentCount} {t.agentCount === 1 ? 'agent' : 'agents'}
                {t.env ? ` · ${t.env}` : ''}
                {t.instances && t.instances.length > 0 ?
                  ` · ${t.instances.length} ${t.instances.length === 1 ? 'instance' : 'instances'}` : ''}
              </div>
            </div>
            {COLUMNS.map((c) => (
              <Cell key={c.key} cell={(t.cells || {})[c.key] || { inherit: true }} />
            ))}
            <div className="text-right">
              <ChevronRight size={14} className="text-v2-ink-muted inline" aria-hidden="true" />
            </div>
          </div>
        ))}
      </Card>
      )}

      {/* Legend                                                       */}
      <Card padding="sm" className="mt-3">
        <div className="flex items-center gap-5 text-[11px] text-v2-ink-secondary">
          <div className="flex items-center gap-1.5">
            <ArrowUp size={12} className="text-v2-action" aria-hidden="true" />
            <span><strong className="text-v2-ink">inherit</strong> — value comes from parent scope</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Pencil size={12} className="text-v2-trust" aria-hidden="true" />
            <span><strong className="text-v2-ink">override</strong> — local value overrides parent</span>
          </div>
        </div>
      </Card>
    </V2PageShell>
  );
}
